// Returning from trading accounts keeps this list in memory and does not call getBrokerTrees again.
import React, { useCallback, useMemo, useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Autocomplete,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  Checkbox,
  Select,
  CircularProgress,
  TableSortLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";

import IconComponent from "../Icons/IconComponent";
import BrokerTreesService from "../../Endpoints/BrokerTrees";
import brokersService from "../../Endpoints/BrokersIBs";

import {
  PageRoot,
  MainCard__body,
  TitleWrap,
  Toolbar,
  SearchBox,
  FiltersWrap,
  HeaderCell,
  BodyCell,
  Footer,
  TotalText,
} from "./Styles";
import * as Style from "./Styles";
import * as BrokerStyles from "../Broker/Styles.js";
import {
  buildTableRowsPerPageOptions,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";

const TREE_SORT_COLUMNS = [
  { field: "id", label: "Tree ID", minWidth: 90, width: 110 },
  { field: "name", label: "Tree name", minWidth: 160, width: 190 },
  { field: "ownerType", label: "Broker Owner", minWidth: 150, width: 180 },
  { field: "masterIbName", label: "Master IB", minWidth: 150, width: 190 },
  { field: "ownerName", label: "IB Owner", minWidth: 160, width: 210 },
  { field: "group", label: "Group", minWidth: 120, width: 150 },
  { field: "ccy", label: "CCY", minWidth: 80, width: 95 },
  { field: "server", label: "Server", minWidth: 140, width: 170 },
  { field: "splits", label: "Splits", minWidth: 85, width: 100 },
  { field: "entity", label: "Entity", minWidth: 130, width: 160 },
  { field: "language", label: "Language", minWidth: 120, width: 145 },
  { field: "status", label: "Status", minWidth: 110, width: 130 },
];

const TREE_ACTION_COLUMN = {
  field: "actions",
  label: "Actions",
  minWidth: 90,
  width: 110,
};

const TREE_TABLE_COLUMNS = [...TREE_SORT_COLUMNS, TREE_ACTION_COLUMN];

const REGISTER_URL_BASE = "https://cabinet.tradeviewmarkets.com/en/register/1/live-account";

const TREE_SPLIT_TOGGLE_COLUMNS = [
  { key: "accessIbLink", label: "Access IB link" },
  { key: "clientView", label: "Client view" },
  { key: "showFull", label: "Show full %" },
];

const TREE_SPLIT_COLUMNS = [
  { field: "ibId", label: "IB ID", minWidth: 110, width: 120 },
  { field: "ibName", label: "IB Name", minWidth: 180, width: 220 },
  { field: "ibEmail", label: "IB Email", minWidth: 220, width: 280 },
  { field: "shares", label: "Shares %", minWidth: 140, width: 150 },
  ...TREE_SPLIT_TOGGLE_COLUMNS.map((column) => ({
    field: column.key,
    label: column.label,
    minWidth: 135,
    width: 145,
    align: "center",
  })),
];

const initialTreeSplitColumnWidths = TREE_SPLIT_COLUMNS.reduce((widths, column) => {
  widths[column.field] = column.width;
  return widths;
}, {});

const initialTreeColumnWidths = TREE_TABLE_COLUMNS.reduce((widths, column) => {
  widths[column.field] = column.width;
  return widths;
}, {});

function compareTreeRows(a, b, field, direction) {
  const rawA = a?.[field] ?? "";
  const rawB = b?.[field] ?? "";
  const numA = Number(rawA);
  const numB = Number(rawB);
  const hasNumericA = String(rawA).trim() !== "" && Number.isFinite(numA);
  const hasNumericB = String(rawB).trim() !== "" && Number.isFinite(numB);

  let result = 0;

  if (hasNumericA && hasNumericB) {
    result = numA - numB;
  } else if (hasNumericA) {
    result = -1;
  } else if (hasNumericB) {
    result = 1;
  } else {
    result = String(rawA).localeCompare(String(rawB), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }

  return direction === "desc" ? -result : result;
}

const toNumberOrNull = (value) => {
  const textValue = String(value ?? "").trim();

  if (!textValue || textValue === "-") {
    return null;
  }

  const numberValue = Number(textValue);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const cleanTreeText = (value, fallback = "") => {
  if (value === null || value === undefined || value === "-") return fallback;
  return String(value);
};

const normalizeShareInput = (value) => {
  const cleaned = String(value ?? "").replace(/[^0-9.]/g, "");
  const [integerPart, ...decimalParts] = cleaned.split(".");

  if (!decimalParts.length) return integerPart;

  return `${integerPart}.${decimalParts.join("")}`;
};

const normalizeUserType = (value) => String(value ?? "").trim().toUpperCase();

const isIbUser = (value) => {
  const normalized = normalizeUserType(value);

  return (
    normalized === "IB" ||
    normalized === "INTRODUCING_BROKER" ||
    normalized === "INTRODUCER" ||
    normalized === "INTRODUCINGBROKER"
  );
};

const normalizeBrokerStatus = (value) => {
  const normalized = String(value ?? "").trim().toUpperCase();
  if (normalized === "ENABLED") return "ACTIVE";
  if (normalized === "DISABLED") return "INACTIVE";
  return normalized;
};

const formatIbOptionLabel = (option) => {
  if (!option) return "";
  const name = String(option?.name ?? "").trim();
  const id = String(option?.id ?? "").trim();
  if (name && id) return `${name} (${id})`;
  return name || id;
};

function getIbOwnersFromRow(row) {
  const rawOwners = Array.isArray(row?.rawTree?.ib_owners)
    ? row.rawTree.ib_owners
    : [];

  return rawOwners
    .map((owner) => ({
      id: owner?.id ? String(owner.id) : "",
      name: owner?.name ?? "",
    }))
    .filter((owner) => owner.id);
}

const mapTreeOwnersToPayload = (owners) => {
  if (!Array.isArray(owners)) return [];

  return owners
    .map((owner) => {
      const ownerId = toNumberOrNull(owner?.user?.id);

      if (ownerId === null || ownerId <= 0) return null;

      return {
        user: {
          id: ownerId,
        },
        percentage: Number(owner?.percentage ?? 0),
      };
    })
    .filter(Boolean);
};

const mapSplitSettingsToPayload = (splitSettings) => {
  if (!Array.isArray(splitSettings)) return [];

  return splitSettings.map((split) => ({
    user: {
      id: Number(split.ibId),
    },
    percentage: Number(split.shares ?? 0),
    client_view_flag: Boolean(split.clientView),
    referral_link_flag: Boolean(split.accessIbLink),
    show_full_flag: Boolean(split.showFull),
  }));
};

const getTreeApiErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data ?? error?.data ?? null;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  return (
    responseData?.message ||
    responseData?.data?.message ||
    responseData?.error ||
    error?.message ||
    fallback
  );
};

const buildTreeDetailsPayload = (row, treeName, splitSettings, ownership = {}) => {
  const rawTree = row?.rawTree ?? {};
  const treeId = toNumberOrNull(rawTree?.tree_id ?? row?.id);
  const masterIbId = toNumberOrNull(
    ownership.masterIbId ?? rawTree?.master_ib?.id ?? row?.masterIbId
  );
  const ibOwnersSource = ownership.ibOwners ?? rawTree?.ib_owners ?? [];
  const ibOwnersPayload = ibOwnersSource
    .map((owner) => ({ id: toNumberOrNull(owner?.id) }))
    .filter((owner) => owner.id !== null && owner.id > 0);
  const ownersPayload = mapTreeOwnersToPayload(rawTree?.owners);

  const payload = {
    tree_id: treeId,
    tree_name: String(treeName ?? "").trim(),
    master_ib: masterIbId === null ? null : { id: masterIbId },
    ib_owners: ibOwnersPayload,
    group: cleanTreeText(rawTree?.group ?? row?.group),
    server: cleanTreeText(rawTree?.server ?? row?.server, "MT4 LTD Demo"),
    entity: cleanTreeText(rawTree?.entity ?? row?.entity),
    language: cleanTreeText(rawTree?.language ?? row?.language),
    status:
      typeof rawTree?.status === "boolean"
        ? rawTree.status
        : row?.status === "Enabled",
    splits: mapSplitSettingsToPayload(splitSettings),
  };

  if (ownersPayload.length) {
    payload.owners = ownersPayload;
  }

  return payload;
};

const buildRawTreeWithModalUpdates = (row, payload, splitSettings, ownership = {}) => {
  const rawTree = row?.rawTree ?? {};
  const rawSplits = Array.isArray(rawTree?.splits) ? rawTree.splits : [];
  const rawMasterIb = rawTree?.master_ib ?? null;
  const nextMasterIbId = toNumberOrNull(payload?.master_ib?.id);
  const ibOwnersSource = ownership.ibOwners ?? rawTree?.ib_owners ?? [];

  return {
    ...rawTree,
    tree_name: payload.tree_name,
    master_ib:
      nextMasterIbId === null
        ? null
        : {
            ...(rawMasterIb ?? {}),
            id: nextMasterIbId,
            name: ownership.masterIbName ?? rawMasterIb?.name ?? "",
          },
    ib_owners: ibOwnersSource
      .map((owner) => ({
        ...owner,
        id: toNumberOrNull(owner?.id),
        name: owner?.name ?? "",
      }))
      .filter((owner) => owner.id !== null && owner.id > 0),
    group: payload.group,
    server: payload.server,
    entity: payload.entity,
    language: payload.language,
    status: payload.status,
    splits: splitSettings.map((split, index) => ({
      ...rawSplits[index],
      user: {
        ...(rawSplits[index]?.user ?? {}),
        id: Number(split.ibId),
        name: split.ibName,
        email: split.ibEmail,
      },
      percentage: Number(split.shares ?? 0),
      client_view_flag: Boolean(split.clientView),
      referral_link_flag: Boolean(split.accessIbLink),
      show_full_flag: Boolean(split.showFull),
    })),
  };
};

function joinUserNames(users, fallback = "-") {
  if (!Array.isArray(users) || users.length === 0) return fallback;

  const names = users
    .map((user) => String(user?.name ?? "").trim())
    .filter(Boolean);

  if (!names.length) return fallback;

  return Array.from(new Set(names)).join(", ");
}

function getIbOwnerNameFromSplits(splits, fallback = "-") {
  if (!Array.isArray(splits) || splits.length === 0) return fallback;

  return joinUserNames(
    splits.map((split) => split?.user),
    fallback
  );
}

function getIbOwnerName(rawTree, fallback = "-") {
  if (Array.isArray(rawTree?.ib_owners) && rawTree.ib_owners.length) {
    return joinUserNames(rawTree.ib_owners, fallback);
  }

  return getIbOwnerNameFromSplits(rawTree?.splits, fallback);
}

const TREES_VIEW_CACHE_KEY = "brokerTrees.viewCache.v1";

const treesViewMemory = {
  hydrated: false,
  forceRefresh: false,
  rows: [],
  search: "",
  page: 0,
  rowsPerPage: null,
  sortField: "",
  sortDirection: "asc",
  selectedRowId: null,
  columnWidths: null,
  scrollPosition: null,
  error: "",
};

function readTreesCache() {
  try {
    const raw = sessionStorage.getItem(TREES_VIEW_CACHE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || !Array.isArray(saved.rows)) return null;
    return saved;
  } catch (error) {
    return null;
  }
}

function persistTreesMemory() {
  try {
    sessionStorage.setItem(
      TREES_VIEW_CACHE_KEY,
      JSON.stringify({
        rows: treesViewMemory.rows,
        search: treesViewMemory.search,
        page: treesViewMemory.page,
        rowsPerPage: treesViewMemory.rowsPerPage,
        sortField: treesViewMemory.sortField,
        sortDirection: treesViewMemory.sortDirection,
        selectedRowId: treesViewMemory.selectedRowId,
        columnWidths: treesViewMemory.columnWidths,
        scrollPosition: treesViewMemory.scrollPosition,
        error: treesViewMemory.error || "",
      })
    );
  } catch (error) {
    // The in-memory copy still keeps the place if storage is full.
  }
}

function applySavedTreesView(saved, restoreRows) {
  if (!saved) return treesViewMemory;

  treesViewMemory.search = saved.search || "";
  treesViewMemory.page = Number(saved.page) || 0;
  treesViewMemory.rowsPerPage = saved.rowsPerPage || null;
  treesViewMemory.sortField = saved.sortField || "";
  treesViewMemory.sortDirection = saved.sortDirection || "asc";
  treesViewMemory.selectedRowId = saved.selectedRowId ?? null;
  treesViewMemory.columnWidths = saved.columnWidths || null;
  treesViewMemory.scrollPosition = saved.scrollPosition || null;
  treesViewMemory.error = saved.error || "";

  if (restoreRows && Array.isArray(saved.rows) && saved.rows.length) {
    treesViewMemory.rows = saved.rows;
    treesViewMemory.hydrated = true;
  }

  return treesViewMemory;
}

function bootstrapTreesMemory() {
  if (treesViewMemory.hydrated && treesViewMemory.rows.length) return treesViewMemory;

  let skipNextFetch = false;
  try {
    skipNextFetch = sessionStorage.getItem("brokerTrees.skipNextFetch") === "1";
    if (skipNextFetch) sessionStorage.removeItem("brokerTrees.skipNextFetch");
  } catch (error) {
    skipNextFetch = false;
  }

  return applySavedTreesView(readTreesCache(), skipNextFetch || treesViewMemory.hydrated);
}

function getTreesMemory() {
  return treesViewMemory;
}

function findScrollParent(anchor) {
  let current = anchor?.parentElement || null;

  while (current && current !== document.body && current !== document.documentElement) {
    const style = window.getComputedStyle(current);
    const overflowY = `${style.overflowY} ${style.overflow}`;
    const canScroll =
      /(auto|scroll)/.test(overflowY) && current.scrollHeight > current.clientHeight + 1;

    if (canScroll) return current;
    current = current.parentElement;
  }

  return null;
}

function readScrollPosition(anchor) {
  const scroller = findScrollParent(anchor);

  if (!scroller) {
    return {
      top: window.scrollY || document.documentElement.scrollTop || 0,
      left: window.scrollX || document.documentElement.scrollLeft || 0,
      mode: "window",
    };
  }

  return {
    top: scroller.scrollTop,
    left: scroller.scrollLeft,
    mode: "element",
  };
}

function restoreScrollPosition(anchor, position) {
  if (!position) return;

  const apply = () => {
    const scroller = findScrollParent(anchor);

    if (position.mode === "element" && scroller) {
      scroller.scrollTop = position.top || 0;
      scroller.scrollLeft = position.left || 0;
      return;
    }

    window.scrollTo(position.left || 0, position.top || 0);
  };

  apply();
  window.requestAnimationFrame(apply);
}

function mapApiTreeToRow(t) {
  const ownerUser = t?.owner ?? null;
  const masterIbName = t?.master_ib?.name ?? "-";
  const ibOwnerName = getIbOwnerName(t, masterIbName);

  return {
    id: t?.tree_id ?? "-",
    name: t?.tree_name ?? "-",
    ownerType: ownerUser?.name ?? "-",
    ownerName: ibOwnerName,
    group: t?.group ?? "-",
    ccy: t?.currency ?? "-",
    server: t?.server ?? "-",
    splits: Number(t?.split_quantity ?? 0),
    entity: t?.entity ?? "-",
    language: t?.language ?? "-",
    status: t?.status ? "Enabled" : "Disabled",
    statusValue: Boolean(t?.status),
    masterIbId: t?.master_ib?.id ?? null,
    masterIbName,

    mainOwner: t?.owners?.[0]?.user?.name ?? ownerUser?.name ?? "-",
    mcso: t?.mcso ?? "UNKNOWN",
    leverage: String(t?.leverage ?? "-"),
    rawTree: t,

    splitSettings: Array.isArray(t?.splits)
      ? t.splits.map((s) => ({
          ibId: s?.user?.id ?? "-",
          ibName: s?.user?.name ?? "-",
          ibEmail: s?.user?.email ?? "-",
          shares: Number(s?.percentage ?? 0),
          accessIbLink: Boolean(s?.referral_link_flag),
          clientView: Boolean(s?.client_view_flag),
          showFull: Boolean(s?.show_full_flag),
        }))
      : [],
  };
}
function TreeDetailsModal({ open, onClose, row, onSave, saving, error }) {
  const [treeName, setTreeName] = useState(row?.name ?? "");
  const [isTreeNameEditing, setIsTreeNameEditing] = useState(false);
  const [items, setItems] = useState(row?.splitSettings ?? []);
  const [splitPage, setSplitPage] = useState(0);
  const [splitRowsPerPage, setSplitRowsPerPage] = useState(
    TABLE_INITIAL_ROWS_PER_PAGE
  );
  const [splitColumnWidths, setSplitColumnWidths] = useState(
    initialTreeSplitColumnWidths
  );
  const splitRowsOptions = useMemo(
    () => buildTableRowsPerPageOptions([5, 7, 10, 25, 50, 100]),
    []
  );

  const [masterIbId, setMasterIbId] = useState(
    row?.masterIbId ? String(row.masterIbId) : ""
  );
  const [masterIbName, setMasterIbName] = useState(row?.masterIbName ?? "");
  const [ibOwners, setIbOwners] = useState(() => getIbOwnersFromRow(row));
  const [ibOptions, setIbOptions] = useState([]);
  const [ibOptionsLoading, setIbOptionsLoading] = useState(false);
  const [ibOptionsError, setIbOptionsError] = useState("");

  useEffect(() => {
    let alive = true;

    const loadIbOptions = async () => {
      setIbOptionsLoading(true);
      setIbOptionsError("");

      try {
        const response = await brokersService.BrokersIBs();
        const users = Array.isArray(response?.data?.data?.users)
          ? response.data.data.users
          : [];

        const enabledIbs = users
          .map((user) => ({
            id: user?.id ?? "",
            name: user?.name ?? "",
            userType: normalizeUserType(
              user?.user_type ?? user?.userType ?? user?.type ?? ""
            ),
            status: normalizeBrokerStatus(
              user?.account_status ?? user?.status ?? user?.status_backend ?? ""
            ),
          }))
          .filter((user) => toNumberOrNull(user.id))
          .filter((user) => user.status === "ACTIVE")
          .filter((user) => isIbUser(user.userType))
          .sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
            })
          );

        if (!alive) return;
        setIbOptions(enabledIbs);
      } catch (loadError) {
        if (!alive) return;
        setIbOptions([]);
        setIbOptionsError("We couldn't load enabled IBs.");
      } finally {
        if (alive) setIbOptionsLoading(false);
      }
    };

    loadIbOptions();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setTreeName(row?.name ?? "");
    setIsTreeNameEditing(false);
    setItems(row?.splitSettings ?? []);
    setSplitPage(0);
    setSplitRowsPerPage(TABLE_INITIAL_ROWS_PER_PAGE);
    setMasterIbId(row?.masterIbId ? String(row.masterIbId) : "");
    setMasterIbName(row?.masterIbName ?? "");
    setIbOwners(getIbOwnersFromRow(row));
  }, [row]);

  useEffect(() => {
    setSplitPage(0);
  }, [splitRowsPerPage]);

  const toggleItem = (idx, field) => {
    if (saving) return;

    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: !it[field] } : it))
    );
  };

  const setItemField = (idx, field, value) => {
    if (saving) return;

    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it))
    );
  };

  const canSave = String(treeName).trim().length > 0 && !saving;
  const handleTreeNameEditStart = () => {
    if (saving) return;
    setIsTreeNameEditing(true);
  };

  const selectedMasterIbOption = useMemo(() => {
    const matched = ibOptions.find((ib) => String(ib.id) === String(masterIbId));
    if (matched) return matched;
    if (!String(masterIbId).trim()) return null;

    return { id: masterIbId, name: masterIbName || `IB ${masterIbId}` };
  }, [ibOptions, masterIbId, masterIbName]);

  const masterIbAutocompleteOptions = useMemo(() => {
    if (!selectedMasterIbOption) return ibOptions;

    const exists = ibOptions.some(
      (ib) => String(ib.id) === String(selectedMasterIbOption.id)
    );

    return exists ? ibOptions : [selectedMasterIbOption, ...ibOptions];
  }, [ibOptions, selectedMasterIbOption]);

  const handleMasterIbChange = (selectedOption) => {
    setMasterIbId(selectedOption?.id ? String(selectedOption.id) : "");
    setMasterIbName(selectedOption?.name ?? "");
  };

  const selectedIbOwnerOptions = useMemo(() => {
    return ibOwners.map((owner) => {
      const matched = ibOptions.find((ib) => String(ib.id) === String(owner.id));
      return matched || { id: owner.id, name: owner.name || `IB ${owner.id}` };
    });
  }, [ibOwners, ibOptions]);

  const ibOwnersAutocompleteOptions = useMemo(() => {
    const missingSelected = selectedIbOwnerOptions.filter(
      (selected) => !ibOptions.some((ib) => String(ib.id) === String(selected.id))
    );

    return [...missingSelected, ...ibOptions];
  }, [ibOptions, selectedIbOwnerOptions]);

  const handleIbOwnersChange = (selectedOptions) => {
    setIbOwners(
      (selectedOptions || []).map((option) => ({
        id: String(option.id),
        name: option?.name ?? "",
      }))
    );
  };

  const splitTableMinWidth = useMemo(() => {
    return TREE_SPLIT_COLUMNS.reduce(
      (total, column) => total + (splitColumnWidths[column.field] || column.width),
      0
    );
  }, [splitColumnWidths]);

  const splitColumnCellSx = useMemo(() => {
    return TREE_SPLIT_COLUMNS.reduce((styles, column) => {
      const width = splitColumnWidths[column.field] || column.width;

      styles[column.field] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };

      return styles;
    }, {});
  }, [splitColumnWidths]);

  const handleSplitColumnResizeStart = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = splitColumnWidths[column.field] || column.width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const nextWidth = Math.max(
          column.minWidth,
          startWidth + moveEvent.clientX - startX
        );

        setSplitColumnWidths((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [splitColumnWidths]
  );

  const handleSplitColumnResizeKeyDown = useCallback((event, column) => {
    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (!direction) return;

    event.preventDefault();
    event.stopPropagation();

    setSplitColumnWidths((prev) => {
      const currentWidth = prev[column.field] || column.width;

      return {
        ...prev,
        [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
      };
    });
  }, []);

  const splitTotal = useMemo(() => {
    return items.reduce((total, item) => {
      const value = Number(item?.shares);
      return total + (Number.isFinite(value) ? value : 0);
    }, 0);
  }, [items]);

  const formattedSplitTotal = useMemo(() => {
    if (!Number.isFinite(splitTotal)) return "0";

    return Number(splitTotal.toFixed(2))
      .toString()
      .replace(/\.0+$/, "");
  }, [splitTotal]);

  const splitTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(items.length / splitRowsPerPage));
  }, [items.length, splitRowsPerPage]);

  useEffect(() => {
    setSplitPage((prev) => Math.min(prev, splitTotalPages - 1));
  }, [splitTotalPages]);

  const paginatedSplitItems = useMemo(() => {
    const start = splitPage * splitRowsPerPage;
    return items
      .map((item, index) => ({ item, index }))
      .slice(start, start + splitRowsPerPage);
  }, [items, splitPage, splitRowsPerPage]);

  const topLabel = { fontWeight: 800, fontSize: 13, color: "#111827" };
  const topValue = { fontSize: 14, color: "#374151" };

  const checkboxSx = {
    p: 0.5,
    color: "#111827",
    "&.Mui-checked": { color: "#15803D" },
    "&.MuiCheckbox-indeterminate": { color: "#15803D" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        "& .MuiBackdrop-root": { backgroundColor: "rgba(17, 24, 39, .35)" },
      }}
      PaperProps={{
        sx: {
          position: "fixed",
          top: 24,
          right: 24,
          bottom: 24,
          m: 0,
          width: { xs: "calc(100% - 48px)", sm: 620, md: 760 },
          maxWidth: "100%",
          marginBottom: "20px",
          borderRadius: 10,
          overflow: "hidden",
          height: "95vh",
          boxShadow: "0px 30px 60px rgba(0,0,0,.18)",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "white",
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 30, fontWeight: 900, color: "#111827" }}>
              Tree details
            </Typography>
            <Typography
              sx={{ mt: 0.75, fontSize: 18, fontWeight: 700, color: "#111827" }}
            >
              Tree ID #{row?.id ?? "-"}
            </Typography>
          </Box>

          <IconButton onClick={onClose} disabled={saving} sx={{ mt: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, flexShrink: 0 }}>
          <Box
            sx={{
              background: "#EAF1F8",
              borderRadius: 4,
              p: 2.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2.5,
            }}
          >
            <Box>
              <Typography sx={topLabel}>Tree name:</Typography>
              {isTreeNameEditing ? (
                <TextField
                  variant="standard"
                  size="small"
                  value={treeName}
                  onChange={(event) => setTreeName(event.target.value)}
                  onBlur={() => setIsTreeNameEditing(false)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      setIsTreeNameEditing(false);
                    }
                  }}
                  autoFocus
                  fullWidth
                  disabled={saving}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: topValue.fontSize,
                      color: topValue.color,
                    },
                    "& .MuiInputBase-input": {
                      p: 0,
                      height: "auto",
                      fontSize: topValue.fontSize,
                      color: topValue.color,
                    },
                  }}
                />
              ) : (
                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={handleTreeNameEditStart}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleTreeNameEditStart();
                    }
                  }}
                  sx={{
                    ...topValue,
                    cursor: saving ? "default" : "text",
                  }}
                >
                  {treeName || "-"}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography sx={topLabel}>Main owner:</Typography>
              <Typography sx={topValue}>{row?.mainOwner ?? "-"}</Typography>
            </Box>

            <Box>
              <Typography sx={topLabel}>MCSO:</Typography>
              <Typography sx={topValue}>{row?.mcso ?? "-"}</Typography>
            </Box>

            <Box>
              <Typography sx={topLabel}>Leverage:</Typography>
              <Typography sx={topValue}>{row?.leverage ?? "-"}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 3, pt: 2.5, flexShrink: 0 }}>
          <Box
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: 4,
              p: 2.5,
              background: "white",
            }}
          >
            <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 15 }}>
              Ownership
            </Typography>
            <Typography sx={{ mt: 0.5, mb: 2, fontSize: 12, color: "#6B7280" }}>
              Master IB accepts a single IB. IB Owners can include multiple IBs.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1fr)" },
                gap: 2,
              }}
            >
              <Autocomplete
                fullWidth
                size="small"
                options={masterIbAutocompleteOptions}
                value={selectedMasterIbOption}
                loading={ibOptionsLoading}
                disabled={saving}
                noOptionsText={
                  ibOptionsLoading ? "Loading IBs..." : "No enabled IBs found."
                }
                getOptionLabel={(option) => formatIbOptionLabel(option)}
                isOptionEqualToValue={(option, value) =>
                  String(option?.id) === String(value?.id)
                }
                onChange={(_, option) => handleMasterIbChange(option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Master IB"
                    placeholder="Search IB..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                fullWidth
                multiple
                disableCloseOnSelect
                limitTags={2}
                size="small"
                options={ibOwnersAutocompleteOptions}
                value={selectedIbOwnerOptions}
                loading={ibOptionsLoading}
                disabled={saving}
                noOptionsText={
                  ibOptionsLoading ? "Loading IBs..." : "No enabled IBs found."
                }
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  String(option?.id) === String(value?.id)
                }
                onChange={(_, selectedOptions) => handleIbOwnersChange(selectedOptions)}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box component="li" key={key} {...optionProps}>
                      <Checkbox
                        checked={selected}
                        sx={{ ...checkboxSx, mr: 1 }}
                      />
                      {formatIbOptionLabel(option)}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="IB Owners"
                    placeholder={selectedIbOwnerOptions.length ? "" : "Search IB..."}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {ibOptionsError ? (
              <Typography sx={{ mt: 1.5, color: "#B91C1C", fontWeight: 800, fontSize: 12 }}>
                {ibOptionsError}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            px: 3,
            pt: 3,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 18 }}>
              Tree split settings
            </Typography>

            <Box
              sx={{
                px: 1.5,
                py: 0.9,
                borderRadius: 999,
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: "#475569",
                  whiteSpace: "nowrap",
                }}
              >
                {`${items.length} split${items.length === 1 ? "" : "s"} · ${formattedSplitTotal}% total`}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: "1px solid #E5E7EB",
              borderRadius: 4,
              background: "white",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {!items.length ? (
              <Box sx={{ py: 6, textAlign: "center", color: "#64748B" }}>
                No split settings found.
              </Box>
            ) : (
              <>
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  <Table
                    stickyHeader
                    size="small"
                    sx={{ minWidth: splitTableMinWidth, tableLayout: "fixed" }}
                  >
                    <colgroup>
                      {TREE_SPLIT_COLUMNS.map((column) => (
                        <col
                          key={column.field}
                          style={{
                            width: `${splitColumnWidths[column.field] || column.width}px`,
                          }}
                        />
                      ))}
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        {TREE_SPLIT_COLUMNS.map((column) => (
                          <HeaderCell
                            key={column.field}
                            align={column.align || "left"}
                            sx={{
                              ...splitColumnCellSx[column.field],
                              position: "relative",
                            }}
                          >
                            <Box component="span" title={column.label}>
                              {column.label}
                            </Box>
                            <Box
                              component="span"
                              role="separator"
                              aria-label={`Resize ${column.label} column`}
                              aria-orientation="vertical"
                              aria-valuemin={column.minWidth}
                              aria-valuenow={
                                splitColumnWidths[column.field] || column.width
                              }
                              tabIndex={0}
                              onPointerDown={(event) =>
                                handleSplitColumnResizeStart(event, column)
                              }
                              onKeyDown={(event) =>
                                handleSplitColumnResizeKeyDown(event, column)
                              }
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                width: "12px",
                                height: "100%",
                                cursor: "col-resize",
                                touchAction: "none",
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  top: "22%",
                                  bottom: "22%",
                                  left: "50%",
                                  width: "2px",
                                  transform: "translateX(-50%)",
                                  borderRadius: "2px",
                                  backgroundColor: "rgba(21, 32, 42, 0.22)",
                                },
                                "&:hover::after, &:focus-visible::after": {
                                  backgroundColor: "#15202A",
                                },
                                "&:focus-visible": {
                                  outline: "2px solid #15202A",
                                  outlineOffset: "-2px",
                                },
                              }}
                            />
                          </HeaderCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedSplitItems.map(({ item: it, index: idx }) => (
                        <TableRow key={`${it.ibId}-${idx}`} hover>
                          <BodyCell
                            sx={{
                              ...splitColumnCellSx.ibId,
                              fontWeight: 800,
                              color: "#111827",
                            }}
                          >
                            #{it.ibId}
                          </BodyCell>

                          <BodyCell
                            sx={{ ...splitColumnCellSx.ibName, color: "#111827" }}
                          >
                            <Box
                              component="span"
                              title={it.ibName}
                              sx={{
                                display: "block",
                                width: "100%",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {it.ibName}
                            </Box>
                          </BodyCell>

                          <BodyCell
                            sx={{ ...splitColumnCellSx.ibEmail, color: "#475569" }}
                          >
                            <Box
                              component="span"
                              title={it.ibEmail}
                              sx={{
                                display: "block",
                                width: "100%",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {it.ibEmail}
                            </Box>
                          </BodyCell>

                          <BodyCell sx={splitColumnCellSx.shares}>
                            <TextField
                              variant="standard"
                              size="small"
                              value={it.shares ?? ""}
                              onChange={(event) =>
                                setItemField(
                                  idx,
                                  "shares",
                                  normalizeShareInput(event.target.value)
                                )
                              }
                              disabled={saving}
                              InputProps={{
                                disableUnderline: true,
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{
                                      ml: 0.5,
                                      "& .MuiTypography-root": {
                                        color: "#111827",
                                        fontSize: 14,
                                        fontWeight: 700,
                                      },
                                    }}
                                  >
                                    %
                                  </InputAdornment>
                                ),
                              }}
                              inputProps={{
                                inputMode: "decimal",
                                "aria-label": `Shares percentage for ${it.ibName}`,
                              }}
                              sx={{
                                width: "100%",
                                px: 1.25,
                                py: 0.6,
                                borderRadius: 2,
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                "& .MuiInputBase-root": {
                                  color: "#111827",
                                  fontSize: 14,
                                  fontWeight: 700,
                                },
                                "& .MuiInputBase-input": {
                                  p: 0,
                                  height: "auto",
                                  color: "#111827",
                                  fontSize: 14,
                                  fontWeight: 700,
                                },
                              }}
                            />
                          </BodyCell>

                          {TREE_SPLIT_TOGGLE_COLUMNS.map((column) => {
                            const checked = Boolean(it[column.key]);

                            return (
                              <BodyCell
                                key={column.key}
                                align="center"
                                sx={splitColumnCellSx[column.key]}
                              >
                                <Checkbox
                                  checked={checked}
                                  onChange={() => toggleItem(idx, column.key)}
                                  disabled={saving}
                                  size="small"
                                  sx={checkboxSx}
                                  inputProps={{
                                    "aria-label": `${column.label} for ${it.ibName}`,
                                  }}
                                />
                              </BodyCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                    borderTop: "1px solid #E5E7EB",
                    background: "#fff",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                      Total: {items.length}
                    </Typography>

                    <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

                    <Select
                      size="small"
                      value={splitRowsPerPage}
                      onChange={(event) =>
                        setSplitRowsPerPage(Number(event.target.value))
                      }
                      sx={{
                        height: 30,
                        minWidth: 68,
                        borderRadius: 2,
                        ".MuiSelect-select": { py: 0.5, fontSize: 13 },
                      }}
                      MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
                    >
                      {splitRowsOptions.map((rowsOption) => (
                        <MenuItem key={rowsOption} value={rowsOption}>
                          {rowsOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                      Pages:&nbsp;<b style={{ color: "#111827" }}>{splitTotalPages}</b>
                    </Typography>

                    <Button
                      size="small"
                      disabled={splitPage === 0 || saving}
                      onClick={() => setSplitPage((prev) => Math.max(0, prev - 1))}
                      sx={{
                        minWidth: 30,
                        height: 30,
                        borderRadius: "10px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        color: splitPage === 0 || saving ? "#9CA3AF" : "#374151",
                        boxShadow: "none",
                        padding: 0,
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                          borderColor: "#D1D5DB",
                          boxShadow: "none",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#FFFFFF",
                          borderColor: "#E5E7EB",
                          color: "#9CA3AF",
                          opacity: 1,
                        },
                      }}
                    >
                      <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                    </Button>

                    <Button
                      size="small"
                      disabled={splitPage >= splitTotalPages - 1 || saving}
                      onClick={() =>
                        setSplitPage((prev) => Math.min(splitTotalPages - 1, prev + 1))
                      }
                      sx={{
                        minWidth: 30,
                        height: 30,
                        borderRadius: "10px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        color:
                          splitPage >= splitTotalPages - 1 || saving
                            ? "#9CA3AF"
                            : "#374151",
                        boxShadow: "none",
                        padding: 0,
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                          borderColor: "#D1D5DB",
                          boxShadow: "none",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#FFFFFF",
                          borderColor: "#E5E7EB",
                          color: "#9CA3AF",
                          opacity: 1,
                        },
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                    </Button>
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Divider />

        {error ? (
          <Typography
            sx={{
              px: 3,
              pt: 2,
              color: "#B91C1C",
              fontSize: 13,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {error}
          </Typography>
        ) : null}

        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontWeight: 800,
              borderColor: "#111827",
              color: "#111827",
              background: "white",
              "&:hover": { borderColor: "#111827", background: "#F9FAFB" },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={() =>
              onSave?.({
                treeName,
                splitSettings: items,
                masterIbId,
                masterIbName,
                ibOwners,
              })
            }
            disabled={!canSave}
            sx={{
              borderRadius: 3,
              px: 5,
              py: 1.5,
              textTransform: "none",
              fontWeight: 900,
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
function BrokerTrees() {
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const memory = bootstrapTreesMemory();
  const appliedSearch = useRef(memory.search || "");
  const appliedRowsPerPage = useRef(memory.rowsPerPage || TABLE_INITIAL_ROWS_PER_PAGE);
  const selectedRowSx = {
    backgroundColor: "#ECF1F7",
  };

  const [search, setSearch] = useState(memory.search || "");

  const [rows, setRows] = useState(() => (Array.isArray(memory.rows) ? memory.rows : []));
  const [loading, setLoading] = useState(() => !memory.hydrated);
  const [error, setError] = useState(memory.error || "");

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(memory.selectedRowId ?? null);
  const [copyLinkFeedback, setCopyLinkFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const menuOpen = Boolean(menuAnchorEl);

  const [openDetails, setOpenDetails] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [deletedTreeId, setDeletedTreeId] = useState(null);

  const ROWS_OPTIONS = buildTableRowsPerPageOptions([5, 7, 10, 25, 50, 100]);
  const [page, setPage] = useState(memory.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    memory.rowsPerPage || TABLE_INITIAL_ROWS_PER_PAGE
  );
  const [sortField, setSortField] = useState(memory.sortField || "");
  const [sortDirection, setSortDirection] = useState(memory.sortDirection || "asc");
  const [columnWidths, setColumnWidths] = useState(
    () => memory.columnWidths || initialTreeColumnWidths
  );

  const rememberTreesView = useCallback(
    (nextSelectedRowId = selectedRowId) => {
      const current = getTreesMemory();
      current.search = search;
      current.page = page;
      current.rowsPerPage = rowsPerPage;
      current.sortField = sortField;
      current.sortDirection = sortDirection;
      current.selectedRowId = nextSelectedRowId ?? null;
      current.columnWidths = columnWidths;
      current.rows = rows;
      current.error = error;
      current.scrollPosition = readScrollPosition(pageRef.current);
    },
    [
      search,
      page,
      rowsPerPage,
      sortField,
      sortDirection,
      selectedRowId,
      columnWidths,
      rows,
      error,
    ]
  );

  const tableMinWidth = useMemo(() => {
    return TREE_TABLE_COLUMNS.reduce(
      (total, column) => total + (columnWidths[column.field] || column.width),
      0
    );
  }, [columnWidths]);

  const columnCellSx = useMemo(() => {
    return TREE_TABLE_COLUMNS.reduce((styles, column) => {
      const width = columnWidths[column.field] || column.width;

      styles[column.field] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
      };

      return styles;
    }, {});
  }, [columnWidths]);

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = columnWidths[column.field] || column.width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const nextWidth = Math.max(
          column.minWidth,
          startWidth + moveEvent.clientX - startX
        );

        setColumnWidths((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [columnWidths]
  );

  const handleColumnResizeKeyDown = useCallback((event, column) => {
    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (!direction) return;

    event.preventDefault();
    event.stopPropagation();

    setColumnWidths((prev) => {
      const currentWidth = prev[column.field] || column.width;

      return {
        ...prev,
        [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
      };
    });
  }, []);

  useLayoutEffect(() => {
    if (!memory.hydrated || !memory.scrollPosition) return undefined;

    restoreScrollPosition(pageRef.current, memory.scrollPosition);
    const timer = window.setTimeout(() => {
      restoreScrollPosition(pageRef.current, memory.scrollPosition);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [memory]);

  useEffect(() => {
    const scroller = findScrollParent(pageRef.current) || window;
    const saveScroll = () => {
      getTreesMemory().scrollPosition = readScrollPosition(pageRef.current);
    };

    scroller.addEventListener("scroll", saveScroll, { passive: true });
    return () => {
      saveScroll();
      scroller.removeEventListener("scroll", saveScroll);
    };
  }, []);

  useEffect(() => {
    return () => {
      rememberTreesView();
    };
  }, [rememberTreesView]);

  useEffect(() => {
    const current = getTreesMemory();
    // Already visited in this browser tab: do not call the API again.
    // React StrictMode runs effects twice; hydrated is set before any await,
    // so the second run cannot start another request either.
    if (current.hydrated && !current.forceRefresh) {
      current.forceRefresh = false;
      setRows(current.rows);
      setPage(current.page || 0);
      setLoading(false);
      return undefined;
    }

    current.forceRefresh = false;
    let alive = true;

    const load = async () => {
      if (!current.hydrated) {
        setLoading(true);
        setError("");
      }

      try {
        const res = await BrokerTreesService.getBrokerTrees();
        if (!alive) return;
        if (!res) {
          if (!current.hydrated) {
            setRows([]);
            setError("Error loading broker trees");
          }
          return;
        }
        const payload = res?.data?.data ?? res?.data ?? null;
        const apiTrees = Array.isArray(payload?.trees)
          ? payload.trees
          : Array.isArray(payload)
          ? payload
          : payload
          ? [payload]
          : [];
        const mapped = apiTrees.map(mapApiTreeToRow);
        if (!alive) return;
        current.hydrated = true;
        current.rows = mapped;
        current.error = "";
        persistTreesMemory();
        setRows(mapped);
        setPage(current.page || 0);
        setError("");
      } catch (e) {
        if (alive && !current.hydrated) {
          setError(e?.message || "Error loading broker trees");
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (appliedRowsPerPage.current === rowsPerPage) return;
    appliedRowsPerPage.current = rowsPerPage;
    getTreesMemory().page = 0;
    setPage(0);
  }, [rowsPerPage]);

  useEffect(() => {
    if (appliedSearch.current === search) return;
    appliedSearch.current = search;
    getTreesMemory().page = 0;
    setPage(0);
  }, [search]);

  useEffect(() => {
    const current = getTreesMemory();
    current.search = search;
    current.page = page;
    current.rowsPerPage = rowsPerPage;
    current.sortField = sortField;
    current.sortDirection = sortDirection;
    current.selectedRowId = selectedRowId;
    current.columnWidths = columnWidths;
    current.rows = rows;
    current.error = error;
    if (current.hydrated) persistTreesMemory();
  }, [
    search,
    page,
    rowsPerPage,
    sortField,
    sortDirection,
    selectedRowId,
    columnWidths,
    rows,
    error,
  ]);

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    const fields = [
      "id",
      "name",
      "ownerType",
      "masterIbName",
      "ownerName",
      "group",
      "ccy",
      "server",
      "splits",
      "entity",
      "language",
      "status",
    ];

    return rows.filter((row) => {
      const matchMain = fields.some((key) => {
        const v = row?.[key];
        const t = v === null || v === undefined ? "" : String(v).toLowerCase();
        return t.includes(q);
      });

      if (matchMain) return true;

      const ss = Array.isArray(row?.splitSettings) ? row.splitSettings : [];
      return ss.some((s) => {
        const blob = [
          s?.ibId,
          s?.ibName,
          s?.ibEmail,
          s?.shares,
          s?.accessIbLink,
          s?.clientView,
          s?.showFull,
        ]
          .map((x) => (x === null || x === undefined ? "" : String(x).toLowerCase()))
          .join(" ");

        return blob.includes(q);
      });
    });
  }, [rows, search]);

  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;

    return [...filteredData].sort((a, b) =>
      compareTreeRows(a, b, sortField, sortDirection)
    );
  }, [filteredData, sortField, sortDirection]);

  const totalCount = sortedData.length;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / rowsPerPage));
  }, [totalCount, rowsPerPage]);

  useEffect(() => {
    if (!rows.length) return;
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages, rows.length]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const pageItems = useMemo(() => {
    const last = totalPages;
    const current = page + 1;

    if (last <= 4) return Array.from({ length: last }, (_, i) => i + 1);

    if (current <= 2) return [1, 2, 3, 4, "…"];
    if (current >= last - 1) return ["…", last - 3, last - 2, last - 1, last];
    return ["…", current - 1, current, current + 1, "…"];
  }, [page, totalPages]);

  const handleSort = (field) => {
    const isSameField = sortField === field;

    setSortField(field);
    setSortDirection(isSameField && sortDirection === "asc" ? "desc" : "asc");
    setPage(0);
  };

  const arrowBtnSx = (disabled) => ({
    minWidth: 30,
    height: 30,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    color: disabled ? "#9CA3AF" : "#374151",
    boxShadow: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D1D5DB",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
      opacity: 1,
    },
  });

  const handleOpenMenu = (event, row) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedRow(row);
    setSelectedRowId(row?.id ?? null);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedRow(null);
  };

  const handleCopyIbReferralLink = async (row) => {
    const ibId = toNumberOrNull(
      row?.masterIbId ?? row?.rawTree?.master_ib?.id
    );
    const treeId = toNumberOrNull(row?.rawTree?.tree_id ?? row?.id);

    if (treeId === null || treeId <= 0) {
      setCopyLinkFeedback({
        open: true,
        severity: "error",
        message: "This broker tree does not have a tree ID available.",
      });
      handleCloseMenu();
      return;
    }

    const language = cleanTreeText(
      row?.rawTree?.language ?? row?.language
    );
    const platform = cleanTreeText(row?.rawTree?.server ?? row?.server);
    const group = cleanTreeText(row?.rawTree?.group ?? row?.group);
    const registerBase =
      process.env.REACT_APP_WEB_API_URL === "https://api-crm-portal.tradeviewmarkets.com/api"
        ? REGISTER_URL_BASE
        : `${window.location.origin}/register`;
    const queryParams = new URLSearchParams({
      ib: String(ibId !== null && ibId > 0 ? ibId : 0),
      ib_tree: String(treeId),
      lang: language,
      platform,
      group,
    });

    const referralLink = `${registerBase}?${queryParams.toString()}`;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopyLinkFeedback({
        open: true,
        severity: "success",
        message: "Client referral link copied.",
      });
    } catch (copyError) {
      console.error("Unable to copy IB referral link:", copyError);
      setCopyLinkFeedback({
        open: true,
        severity: "error",
        message: "The client referral link could not be copied.",
      });
    } finally {
      handleCloseMenu();
    }
  };

  const openTradingAccounts = (row) => {
    const treeId = row?.id;
    if (!treeId || treeId === "-") {
      console.warn("No selectedRow.id to navigate to trading accounts");
      handleCloseMenu();
      return;
    }

    rememberTreesView(row.id);
    getTreesMemory().forceRefresh = false;
    getTreesMemory().hydrated = true;
    persistTreesMemory();
    try {
      sessionStorage.setItem("brokerTrees.skipNextFetch", "1");
    } catch (error) {
      // ignore
    }
    setSelectedRowId(row.id);
    navigate("/admin/trading-accounts-groups", {
      state: {
        fromTrees: true,
        id_tree: treeId,
        treeId,
        tree: row,
        name: row?.name,
      },
    });
    handleCloseMenu();
  };

  const handleRowClick = (row) => {
    setDetailsError("");
    setSelectedRowId(row?.id ?? null);
    setDetailsRow(row);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    if (savingDetails) return;

    setOpenDetails(false);
    setDetailsRow(null);
    setDetailsError("");
  };

  const handleSaveTreeDetails = async ({
    treeName,
    splitSettings,
    masterIbId,
    masterIbName,
    ibOwners,
  }) => {
    if (!detailsRow || savingDetails) return;

    const nextTreeName = String(treeName ?? "").trim();
    const nextSplitSettings = Array.isArray(splitSettings) ? splitSettings : [];
    const nextIbOwners = Array.isArray(ibOwners) ? ibOwners : [];
    const invalidSplit = nextSplitSettings.find(
      (split) => toNumberOrNull(split?.ibId) === null
    );
    const invalidShare = nextSplitSettings.find(
      (split) => !Number.isFinite(Number(split?.shares ?? 0))
    );

    if (!nextTreeName) {
      setDetailsError("Tree name is required.");
      return;
    }

    if (invalidSplit) {
      setDetailsError("Every split must have a valid IB ID.");
      return;
    }

    if (invalidShare) {
      setDetailsError("Every split must have a valid Shares %.");
      return;
    }

    const payload = buildTreeDetailsPayload(
      detailsRow,
      nextTreeName,
      nextSplitSettings,
      { masterIbId, ibOwners: nextIbOwners }
    );

    if (payload.tree_id === null) {
      setDetailsError("Tree ID is required to update this tree.");
      return;
    }

    try {
      setSavingDetails(true);
      setDetailsError("");

      console.log("UPDATE TREE FROM MODAL PAYLOAD:", JSON.stringify(payload, null, 2));

      const response = await BrokerTreesService.updateBrokerTree(payload, {
        throwOnError: true,
      });
      if (!response) {
        setDetailsError("Failed to update tree.");
        return;
      }

      const updatedRawTree = buildRawTreeWithModalUpdates(
        detailsRow,
        payload,
        nextSplitSettings,
        { masterIbId, masterIbName, ibOwners: nextIbOwners }
      );
      const nextRow = {
        ...detailsRow,
        name: payload.tree_name,
        ownerName: getIbOwnerName(updatedRawTree, detailsRow.ownerName ?? "-"),
        status: payload.status ? "Enabled" : "Disabled",
        statusValue: payload.status,
        masterIbId: payload.master_ib?.id ?? null,
        masterIbName: masterIbName || detailsRow.masterIbName || "",
        splitSettings: nextSplitSettings,
        rawTree: updatedRawTree,
      };

      setRows((prev) =>
        prev.map((row) =>
          Number(row?.id) === Number(detailsRow.id) ? nextRow : row
        )
      );
      setOpenDetails(false);
      setDetailsRow(null);
      setDetailsError("");
    } catch (err) {
      console.error("UPDATE TREE FROM MODAL ERROR:", err);
      setDetailsError(getTreeApiErrorMessage(err, "Failed to update tree."));
    } finally {
      setSavingDetails(false);
    }
  };

  const handleGoCreateTree = () => {
    rememberTreesView();
    getTreesMemory().forceRefresh = true;
    persistTreesMemory();
    navigate("/admin/broker-trees/create");
  };

  const handleOpenDeleteModal = (row) => {
    setRowToDelete(row);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (deleting) return;
    setOpenDeleteModal(false);
    setRowToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete?.id || deleting) return;

    try {
      setDeleting(true);
      setError("");

      console.log("DELETE TREE ID:", rowToDelete.id);

      const response = await BrokerTreesService.deleteBrokerTree(rowToDelete.id);
      if (!response) {
        setError("Failed to delete tree.");
        return;
      }

      console.log("DELETE TREE SUCCESS:", response);

      setRows((prev) =>
        prev.filter((item) => Number(item.id) !== Number(rowToDelete.id))
      );
      setDeletedTreeId(rowToDelete.id);
      setOpenDeleteModal(false);
      setRowToDelete(null);
      setOpenDeleteSuccessModal(true);
    } catch (err) {
      console.error("DELETE TREE ERROR:", err);
      setError(err?.message || "Failed to delete tree.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageRoot>
      <Box ref={pageRef} sx={{ display: "contents" }}>
      <TitleWrap sx={{ mb: 4 }}>
        <Typography sx={Style.typographyClass}>Trees</Typography>
        <Typography variant="body2" color="text.secondary">
          Review existing payout structures and create new ones as needed.
        </Typography>
      </TitleWrap>

      <MainCard__body>
        <Toolbar>
          <SearchBox>
            <TextField
              variant="outlined"
              placeholder="Search..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                ...BrokerStyles.InputStyles,
                width: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </SearchBox>

          <FiltersWrap>
            <Button
              variant="contained"
              color="error"
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : (
                  <IconComponent icon="account_tree" size="18px" />
                )
              }
              onClick={handleGoCreateTree}
              disabled={loading}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                height: "40px",
                padding: "12px",
                backgroundColor: "#EB0000",
                "&:hover": {
                  backgroundColor: "#D50000",
                },
              }}
            >
              {loading ? "Loading..." : "Create a Tree"}
            </Button>
          </FiltersWrap>
        </Toolbar>

        {error ? (
          <Box sx={{ px: 2, py: 2, color: "error.main", fontWeight: 700 }}>
            {error}
          </Box>
        ) : null}

        {loading ? (
          <Box
            sx={{
              px: 2,
              py: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress size={26} />
            <Typography sx={{ fontWeight: 800, color: "#111827" }}>
              Loading...
            </Typography>
          </Box>
        ) : null}

        <TableContainer
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            overflowX: "auto",
          }}
        >
          <Table
            sx={{
              backgroundColor: "#FFFFFF",
              minWidth: tableMinWidth,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              {TREE_TABLE_COLUMNS.map((column) => (
                <col
                  key={column.field}
                  style={{
                    width: `${columnWidths[column.field] || column.width}px`,
                  }}
                />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {TREE_SORT_COLUMNS.map((column) => (
                  <HeaderCell
                    key={column.field}
                    sortDirection={
                      sortField === column.field ? sortDirection : false
                    }
                    sx={{
                      ...columnCellSx[column.field],
                      position: "relative",
                    }}
                  >
                    <TableSortLabel
                      active={sortField === column.field}
                      direction={
                        sortField === column.field ? sortDirection : "asc"
                      }
                      onClick={() => handleSort(column.field)}
                    >
                      {column.label}
                    </TableSortLabel>
                    <Box
                      component="span"
                      role="separator"
                      aria-label={`Resize ${column.label} column`}
                      aria-orientation="vertical"
                      aria-valuemin={column.minWidth}
                      aria-valuenow={columnWidths[column.field] || column.width}
                      tabIndex={0}
                      onPointerDown={(event) =>
                        handleColumnResizeStart(event, column)
                      }
                      onKeyDown={(event) =>
                        handleColumnResizeKeyDown(event, column)
                      }
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "12px",
                        height: "100%",
                        cursor: "col-resize",
                        touchAction: "none",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: "22%",
                          bottom: "22%",
                          left: "50%",
                          width: "2px",
                          transform: "translateX(-50%)",
                          borderRadius: "2px",
                          backgroundColor: "rgba(21, 32, 42, 0.22)",
                        },
                        "&:hover::after, &:focus-visible::after": {
                          backgroundColor: "#15202A",
                        },
                        "&:focus-visible": {
                          outline: "2px solid #15202A",
                          outlineOffset: "-2px",
                        },
                      }}
                    />
                  </HeaderCell>
                ))}
                <HeaderCell
                  align="right"
                  sx={{
                    ...columnCellSx.actions,
                    position: "relative",
                    pr: "40px",
                  }}
                >
                  <Box component="span">Actions</Box>
                  <Box
                    component="span"
                    role="separator"
                    aria-label="Resize Actions column"
                    aria-orientation="vertical"
                    aria-valuemin={TREE_ACTION_COLUMN.minWidth}
                    aria-valuenow={
                      columnWidths[TREE_ACTION_COLUMN.field] ||
                      TREE_ACTION_COLUMN.width
                    }
                    tabIndex={0}
                    onPointerDown={(event) =>
                      handleColumnResizeStart(event, TREE_ACTION_COLUMN)
                    }
                    onKeyDown={(event) =>
                      handleColumnResizeKeyDown(event, TREE_ACTION_COLUMN)
                    }
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: "12px",
                      height: "100%",
                      cursor: "col-resize",
                      touchAction: "none",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: "22%",
                        bottom: "22%",
                        left: "50%",
                        width: "2px",
                        transform: "translateX(-50%)",
                        borderRadius: "2px",
                        backgroundColor: "rgba(21, 32, 42, 0.22)",
                      },
                      "&:hover::after, &:focus-visible::after": {
                        backgroundColor: "#15202A",
                      },
                      "&:focus-visible": {
                        outline: "2px solid #15202A",
                        outlineOffset: "-2px",
                      },
                    }}
                  />
                </HeaderCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading &&
                paginatedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    data-tree-id={row.id}
                    onClick={() => handleRowClick(row)}
                    sx={{
                      cursor: "pointer",
                      ...(selectedRowId === row.id ? selectedRowSx : {}),
                      "&.MuiTableRow-hover:hover": selectedRowSx,
                    }}
                  >
                    <BodyCell sx={columnCellSx.id}>{row.id}</BodyCell>
                    <BodyCell sx={columnCellSx.name}>{row.name}</BodyCell>
                    <BodyCell sx={columnCellSx.ownerType}>{row.ownerType}</BodyCell>
                    <BodyCell sx={columnCellSx.masterIbName}>
                      <Box
                        component="span"
                        title={row.masterIbName}
                        sx={{
                          display: "block",
                          width: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.masterIbName}
                      </Box>
                    </BodyCell>
                    <BodyCell sx={columnCellSx.ownerName}>{row.ownerName}</BodyCell>
                    <BodyCell sx={columnCellSx.group}>{row.group}</BodyCell>
                    <BodyCell sx={columnCellSx.ccy}>{row.ccy}</BodyCell>
                    <BodyCell sx={columnCellSx.server}>{row.server}</BodyCell>
                    <BodyCell sx={columnCellSx.splits}>{row.splits}</BodyCell>
                    <BodyCell sx={columnCellSx.entity}>{row.entity}</BodyCell>
                    <BodyCell sx={columnCellSx.language}>{row.language}</BodyCell>
                    <BodyCell sx={columnCellSx.status}>
                      <Chip
                        label={row.status}
                        size="small"
                        color={row.status === "Enabled" ? "success" : "error"}
                        sx={
                          row.status === "Enabled"
                            ? {
                                backgroundColor: "#E6F5EA",
                                color: "#075C24",
                                "&:hover": {
                                  color: "#FFFFFF",
                                },
                              }
                            : {
                                backgroundColor: "#FFE5E5",
                                color: "#8D0000",
                                "&:hover": {
                                  color: "#FFFFFF",
                                },
                              }
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </BodyCell>

                    <BodyCell align="right" sx={{ ...columnCellSx.actions, pr: "40px" }}>
                      <IconButton size="small" onClick={(e) => handleOpenMenu(e, row)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </BodyCell>
                  </TableRow>
                ))}

              {!loading && !paginatedData.length ? (
                <TableRow>
                  <BodyCell colSpan={13}>
                    <Box sx={{ py: 4, textAlign: "center", color: "#64748B" }}>
                      No results.
                    </Box>
                  </BodyCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          id="tree-actions-menu"
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 220 } }}
        >
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              handleCopyIbReferralLink(selectedRow);
            }}
          >
            Copy link
          </MenuItem>

          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteModal(selectedRow);
              handleCloseMenu();
            }}
          >
            Delete tree
          </MenuItem>

          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              const id = selectedRow?.id;
              if (!id) {
                console.warn("No selectedRow.id to navigate");
                handleCloseMenu();
                return;
              }
              rememberTreesView(id);
              getTreesMemory().forceRefresh = true;
              persistTreesMemory();
              navigate(`/admin/broker-trees/edit/${id}`, {
                state: { row: selectedRow },
              });
              handleCloseMenu();
            }}
          >
            Edit tree
          </MenuItem>

          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              openTradingAccounts(selectedRow);
            }}
          >
            See trading accounts
          </MenuItem>
        </Menu>

        <Snackbar
          open={copyLinkFeedback.open}
          autoHideDuration={3500}
          onClose={() =>
            setCopyLinkFeedback((current) => ({ ...current, open: false }))
          }
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={copyLinkFeedback.severity}
            variant="filled"
            onClose={() =>
              setCopyLinkFeedback((current) => ({ ...current, open: false }))
            }
          >
            {copyLinkFeedback.message}
          </Alert>
        </Snackbar>

        <Footer>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <TotalText>Total: {totalCount}</TotalText>

            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              sx={{
                height: 30,
                minWidth: 68,
                borderRadius: 2,
                ".MuiSelect-select": { py: 0.5, fontSize: 13 },
              }}
              MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
            >
              {ROWS_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>

            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
              Pages:&nbsp;<b style={{ color: "#111827" }}>{totalPages}</b>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                sx={arrowBtnSx(page === 0 || loading)}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </Button>

              {pageItems.map((it, idx) => {
                if (it === "…") {
                  return (
                    <Typography
                      key={`dots-${idx}`}
                      sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}
                    >
                      …
                    </Typography>
                  );
                }

                const pageNumber = it;
                const isActive = pageNumber === page + 1;

                return (
                  <Button
                    key={`p-${pageNumber}`}
                    size="small"
                    disabled={loading}
                    onClick={() => setPage(pageNumber - 1)}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: isActive ? 900 : 700,
                      color: isActive ? "#111827" : "#6B7280",
                      backgroundColor: isActive ? "#EEF2FF" : "transparent",
                      "&:hover": {
                        backgroundColor: isActive ? "#EEF2FF" : "#F3F4F6",
                      },
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    {pageNumber}
                  </Button>
                );
              })}

              <Button
                size="small"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                sx={arrowBtnSx(page >= totalPages - 1 || loading)}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Stack>
        </Footer>
      </MainCard__body>

      <TreeDetailsModal
        open={openDetails}
        onClose={handleCloseDetails}
        row={detailsRow}
        onSave={handleSaveTreeDetails}
        saving={savingDetails}
        error={detailsError}
      />

      <Dialog
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: "calc(100% - 32px)", sm: 420 },
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <DeleteOutlineIcon sx={{ fontSize: 48, color: "#DC2626" }} />
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#111827", textAlign: "center" }}>
            Delete tree
          </Typography>

          <Typography sx={{ mt: 1.5, fontSize: 14, color: "#6B7280", textAlign: "center" }}>
            Are you sure you want to delete this tree?
          </Typography>

          <Typography
            sx={{ mt: 2, fontSize: 14, fontWeight: 800, color: "#111827", textAlign: "center" }}
          >
            Tree ID: #{rowToDelete?.id ?? "-"}
          </Typography>

          <Typography sx={{ mt: 0.5, fontSize: 14, color: "#374151", textAlign: "center" }}>
            {rowToDelete?.name ?? "-"}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1, justifyContent: "center" }}>
          <Button
            variant="outlined"
            onClick={handleCloseDeleteModal}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              borderColor: "#D1D5DB",
              color: "#111827",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontWeight: 900,
              borderRadius: 2,
            }}
          >
            {deleting ? "Deleting..." : "Delete tree"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteSuccessModal}
        onClose={() => setOpenDeleteSuccessModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: { xs: "calc(100% - 32px)", sm: 460 },
          },
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <CheckCircleIcon sx={{ fontSize: 54, color: "#16A34A" }} />
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#111827" }}>
            Tree deleted successfully
          </Typography>

          <Typography sx={{ mt: 1, fontSize: 14, color: "#6B7280" }}>
            The broker tree has been deleted successfully and is no longer available in the system.
          </Typography>

          {deletedTreeId ? (
            <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 800, color: "#111827" }}>
              Tree ID: #{deletedTreeId}
            </Typography>
          ) : null}

          <Box sx={{ mt: 3, display: "flex", gap: 1.2, justifyContent: "center" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setOpenDeleteSuccessModal(false)}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 2,
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      </Box>
    </PageRoot>
  );
}

export default BrokerTrees;